/**
 * Board Component - 3x3 game grid with drag-and-drop for shift phase
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Cell from './Cell';
import WinningLine from './WinningLine';
import { getValidShiftDestinations } from '../engine/gameLogic';
import xImg from '../../../img/x.png';
import oImg from '../../../img/0.png';

const PIECE_IMAGES = { X: xImg, O: oImg };

function Board({
  board,
  phase,
  currentPlayer,
  selectedPiece,
  winningLine,
  isDisabled = false,
  playerRole,
  gameMode,
  onCellClick,
  onShiftDrop,
}) {
  const [dragFrom, setDragFrom] = useState(null);
  const [dragPos, setDragPos] = useState({ x: 0, y: 0 });
  const boardRef = useRef(null);

  // Get all empty cells as valid targets
  const dragTargets =
    dragFrom !== null
      ? getValidShiftDestinations(board, dragFrom)
      : [];

  // The piece being dragged
  const draggedPiece = dragFrom !== null ? board[dragFrom] : null;

  // Check if current user can interact in shift phase
  const canInteractShift = useCallback(() => {
    if (phase !== 'shift') return false;
    if (isDisabled) return false;
    // For offline (Pass & Play), anyone can play
    if (gameMode === 'offline') return true;
    // For other modes, only the player whose turn it is can interact
    return currentPlayer === playerRole;
  }, [phase, isDisabled, gameMode, currentPlayer, playerRole]);

  // Check if a specific piece can be dragged
  const canDragPieceAt = useCallback(
    (index) => {
      if (!canInteractShift()) return false;
      const piece = board[index];
      if (piece === null) return false;
      // Current player can only drag their own pieces (X drags X, O drags O)
      if (piece !== currentPlayer) return false;
      // Must have at least one empty cell to move to
      return getValidShiftDestinations(board, index).length > 0;
    },
    [canInteractShift, board, currentPlayer],
  );

  // Get cell index from screen coordinates
  const getCellIndexFromPoint = useCallback((clientX, clientY) => {
    const el = document.elementFromPoint(clientX, clientY);
    const cell = el?.closest('[data-cell-index]');
    if (!cell) return null;
    const index = Number.parseInt(cell.getAttribute('data-cell-index'), 10);
    return Number.isFinite(index) ? index : null;
  }, []);

  // Complete the drag operation
  const finishDrag = useCallback(
    (clientX, clientY) => {
      if (dragFrom === null) return;

      const targetIndex = getCellIndexFromPoint(clientX, clientY);
      const validTargets = getValidShiftDestinations(board, dragFrom);

      if (targetIndex !== null && validTargets.includes(targetIndex)) {
        onShiftDrop?.(dragFrom, targetIndex);
      }

      setDragFrom(null);
      setDragPos({ x: 0, y: 0 });
    },
    [dragFrom, board, onShiftDrop, getCellIndexFromPoint],
  );

  // Global pointer/touch listeners for drag
  useEffect(() => {
    if (dragFrom === null) return undefined;

    const handlePointerMove = (e) => {
      setDragPos({ x: e.clientX, y: e.clientY });
    };

    const handleTouchMove = (e) => {
      if (e.touches && e.touches.length > 0) {
        const touch = e.touches[0];
        setDragPos({ x: touch.clientX, y: touch.clientY });
      }
    };

    const handlePointerUp = (e) => {
      finishDrag(e.clientX, e.clientY);
    };

    const handleTouchEnd = (e) => {
      if (e.changedTouches && e.changedTouches.length > 0) {
        const touch = e.changedTouches[0];
        finishDrag(touch.clientX, touch.clientY);
      } else {
        setDragFrom(null);
        setDragPos({ x: 0, y: 0 });
      }
    };

    const handleCancel = () => {
      setDragFrom(null);
      setDragPos({ x: 0, y: 0 });
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handleCancel);
    window.addEventListener('touchend', handleTouchEnd);
    window.addEventListener('touchcancel', handleCancel);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handleCancel);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleCancel);
    };
  }, [dragFrom, finishDrag]);

  // Handle cell click (for placement phase)
  const handleCellClick = useCallback(
    (index) => {
      if (isDisabled) return;

      if (phase === 'placement') {
        if (board[index] === null && onCellClick) {
          onCellClick(index);
        }
      }
    },
    [isDisabled, phase, board, onCellClick],
  );

  // Handle piece drag start
  const handlePieceDragStart = useCallback(
    (index, clientX, clientY) => {
      if (!canDragPieceAt(index)) return;
      setDragFrom(index);
      setDragPos({ x: clientX, y: clientY });
    },
    [canDragPieceAt],
  );

  const classNames = [
    'stt-board',
    isDisabled && 'stt-board--disabled',
    dragFrom !== null && 'stt-board--shift-dragging',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        ref={boardRef}
        className={classNames}
        role="grid"
        aria-label="Game board"
      >
        {board.map((value, index) => {
          const isDragSource = dragFrom === index;
          const isDragTarget = dragFrom !== null && dragTargets.includes(index);

          return (
            <Cell
              key={index}
              index={index}
              value={value}
              isSelected={selectedPiece === index || isDragSource}
              isValidTarget={isDragTarget}
              isWinning={winningLine?.includes(index)}
              isDisabled={isDisabled}
              canDragPiece={canDragPieceAt(index)}
              isDragging={isDragSource}
              isDragTarget={isDragTarget}
              onClick={handleCellClick}
              onPieceDragStart={handlePieceDragStart}
            />
          );
        })}
        {winningLine && <WinningLine line={winningLine} />}
      </div>

      {/* Drag Helper - floating piece that follows cursor/finger */}
      {dragFrom !== null && draggedPiece && (
        <div
          className="stt-drag-helper"
          style={{
            position: 'fixed',
            left: dragPos.x,
            top: dragPos.y,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        >
          <img
            src={PIECE_IMAGES[draggedPiece]}
            alt={draggedPiece}
            className={`stt-drag-helper__img stt-drag-helper__img--${draggedPiece.toLowerCase()}`}
          />
        </div>
      )}
    </>
  );
}

export default Board;
